/* eslint-disable prettier/prettier */
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Sidebar } from "primereact/sidebar";
import { confirmDialog } from "primereact/confirmdialog";
import Loader from "common/Loader";
import CustomerService from "services/customer-service";
import TokenService from "services/TokenService";
import { setToaster } from "common/toastStore";

interface IEncompassPayloadItem {
  name: string;
  value: string;
  isCustomField: boolean;
  appendData?: boolean;
}

interface IMortgageClasValidationConfiguration {
  [key: string]: any;
  documentsNotToFeed?: string[];
  excludedDocuments?: string[];
  bondRequiredDocuments?: string[];
  nonBondRequiredDocuments?: string[];
  updateEncompassForMissingDocuments?: boolean;
  encompassPayloadForMissingDocuments?: IEncompassPayloadItem[];
}

type SectionKey =
  | "documentsNotToFeed"
  | "excludedDocuments"
  | "bondRequiredDocuments"
  | "nonBondRequiredDocuments";

const SECTION_DEFINITIONS: { key: SectionKey; title: string }[] = [
  {
    key: "excludedDocuments",
    title: "Exclude download from Encompass",
  },
  {
    key: "documentsNotToFeed",
    title: "Do not send to MortgageClas",
  },
  {
    key: "bondRequiredDocuments",
    title: "Bond Required Documents",
  },
  {
    key: "nonBondRequiredDocuments",
    title: "Non-Bond required documents",
  },
];

const DEFAULT_CONFIGURATION: IMortgageClasValidationConfiguration = {
  documentsNotToFeed: [],
  excludedDocuments: [],
  bondRequiredDocuments: [],
  nonBondRequiredDocuments: [],
};

const deepClone = (data: any) => JSON.parse(JSON.stringify(data));

const normalizeText = (value: string) => value?.trim().toLowerCase();

const prepareConfiguration = (
  config: any,
): IMortgageClasValidationConfiguration => ({
  ...config,
  documentsNotToFeed: Array.isArray(config?.documentsNotToFeed)
    ? config.documentsNotToFeed
    : [],
  excludedDocuments: Array.isArray(config?.excludedDocuments)
    ? config.excludedDocuments
    : [],
  bondRequiredDocuments: Array.isArray(config?.bondRequiredDocuments)
    ? config.bondRequiredDocuments
    : [],
  nonBondRequiredDocuments: Array.isArray(config?.nonBondRequiredDocuments)
    ? config.nonBondRequiredDocuments
    : [],
});

const ManageMortgageClasValidationSettings: FunctionComponent = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [showPendingChanges, setShowPendingChanges] = useState<boolean>(false);
  const [customerApplication, setCustomerApplication] = useState<any>(null);
  const [basicConfiguration, setBasicConfiguration] =
    useState<IMortgageClasValidationConfiguration | null>(null);
  const [initialConfiguration, setInitialConfiguration] =
    useState<IMortgageClasValidationConfiguration | null>(null);
  const [newValues, setNewValues] = useState<Record<SectionKey, string>>({
    documentsNotToFeed: "",
    excludedDocuments: "",
    bondRequiredDocuments: "",
    nonBondRequiredDocuments: "",
  });
  const [searchValues, setSearchValues] = useState<Record<SectionKey, string>>({
    documentsNotToFeed: "",
    excludedDocuments: "",
    bondRequiredDocuments: "",
    nonBondRequiredDocuments: "",
  });

  const hasDuplicate = (
    list: string[],
    value: string,
    ignoreIndex?: number,
  ): boolean => {
    const normalized = normalizeText(value);

    if (!normalized) return false;

    return list.some((item, index) => {
      if (index === ignoreIndex) return false;
      return normalizeText(item) === normalized;
    });
  };

  const updateSectionData = (key: SectionKey, data: string[]) => {
    setBasicConfiguration((prev) => {
      const current = prepareConfiguration(prev || DEFAULT_CONFIGURATION);
      return {
        ...current,
        [key]: data,
      };
    });
  };

  const getCustomerApplication = () => {
    const customerData = TokenService.getCustomerData();
    const customerId = customerData?.[0]?.id;

    if (!customerId) {
      return;
    }

    setLoading(true);
    CustomerService.getCustomerApplications(customerId, 8)
      .then((response: any) => {
        const firstRecord = response?.data?.[0] || null;
        setCustomerApplication(firstRecord);

        if (!firstRecord?.basicConfiguration) {
          const fallbackConfig = prepareConfiguration(DEFAULT_CONFIGURATION);
          setBasicConfiguration(fallbackConfig);
          setInitialConfiguration(deepClone(fallbackConfig));
          return;
        }

        try {
          const config =
            typeof firstRecord.basicConfiguration === "string"
              ? JSON.parse(firstRecord.basicConfiguration)
              : firstRecord.basicConfiguration;
          const prepared = prepareConfiguration(config);

          setBasicConfiguration(prepared);
          setInitialConfiguration(deepClone(prepared));
        } catch (e) {
          console.warn("Error parsing MortgageClas Validation config:", e);
          const fallbackConfig = prepareConfiguration(DEFAULT_CONFIGURATION);
          setBasicConfiguration(fallbackConfig);
          setInitialConfiguration(deepClone(fallbackConfig));
        }
      })
      .catch((e: Error) => {
        console.warn("Error fetching customer application:", e);
        setBasicConfiguration(prepareConfiguration(DEFAULT_CONFIGURATION));
        setInitialConfiguration(prepareConfiguration(DEFAULT_CONFIGURATION));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getCustomerApplication();
  }, []);

  const sectionCards = useMemo(() => {
    const config = prepareConfiguration(basicConfiguration || DEFAULT_CONFIGURATION);

    return SECTION_DEFINITIONS.map((section) => ({
      ...section,
      data: config[section.key] || [],
    }));
  }, [basicConfiguration]);

  const getVisibleSectionData = (key: SectionKey, items: string[]) => {
    const search = searchValues[key]?.trim().toLowerCase();

    if (!search) return items;

    return items.filter((item) => item.toLowerCase().includes(search));
  };

  const hasPendingChanges = useMemo(() => {
    if (!initialConfiguration || !basicConfiguration) return false;

    return (
      JSON.stringify(initialConfiguration) !== JSON.stringify(basicConfiguration)
    );
  }, [initialConfiguration, basicConfiguration]);

  const pendingChanges = useMemo(() => {
    if (!initialConfiguration || !basicConfiguration) return [];

    return SECTION_DEFINITIONS.map((section) => {
      const previousList: string[] = initialConfiguration[section.key] || [];
      const currentList: string[] = basicConfiguration[section.key] || [];
      const added = currentList.filter(
        (item) => !previousList.some((old) => normalizeText(old) === normalizeText(item)),
      );
      const removed = previousList.filter(
        (item) => !currentList.some((curr) => normalizeText(curr) === normalizeText(item)),
      );

      return {
        key: section.key,
        title: section.title,
        added,
        removed,
      };
    }).filter((section) => section.added.length > 0 || section.removed.length > 0);
  }, [initialConfiguration, basicConfiguration]);

  const pendingChangesCount = useMemo(() => {
    return pendingChanges.reduce(
      (total, section) => total + section.added.length + section.removed.length,
      0,
    );
  }, [pendingChanges]);

  const addNewValue = (key: SectionKey) => {
    const incoming = newValues[key]?.trim();

    if (!incoming) {
      setToaster({
        severity: "warn",
        summary: "Validation",
        detail: "Please enter a value before adding.",
      });
      return;
    }

    const list = (basicConfiguration?.[key] || []) as string[];
    if (hasDuplicate(list, incoming)) {
      setToaster({
        severity: "warn",
        summary: "Duplicate Value",
        detail: `Value already exists in ${SECTION_DEFINITIONS.find((s) => s.key === key)?.title}.`,
      });
      return;
    }

    updateSectionData(key, [...list, incoming]);
    setNewValues((prev) => ({ ...prev, [key]: "" }));
  };

  const updateExistingValue = (
    key: SectionKey,
    index: number,
    value: string,
  ) => {
    const list = [...((basicConfiguration?.[key] || []) as string[])];

    if (hasDuplicate(list, value, index)) {
      setToaster({
        severity: "warn",
        summary: "Duplicate Value",
        detail: "This value already exists in the same list.",
      });
      return;
    }

    list[index] = value;
    updateSectionData(key, list);
  };

  const removeValue = (key: SectionKey, index: number) => {
    const sectionTitle =
      SECTION_DEFINITIONS.find((section) => section.key === key)?.title || "this section";

    confirmDialog({
      message: "Are you sure you want to remove this value?",
      header: "Confirm Removal",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        const list = [...((basicConfiguration?.[key] || []) as string[])];
        list.splice(index, 1);
        updateSectionData(key, list);
        setToaster({
          severity: "success",
          summary: "Removed",
          detail: `Value removed from ${sectionTitle}.`,
        });
      },
    });
  };

  const saveChanges = () => {
    if (!customerApplication || !basicConfiguration) {
      setToaster({
        severity: "warn",
        summary: "Missing Data",
        detail: "Customer application data is not available to save.",
      });
      return;
    }

    setSaving(true);

    const payload = {
      ...customerApplication,
      basicConfiguration: JSON.stringify(basicConfiguration),
    };

    CustomerService.saveCustomerApplication(customerApplication.id, payload)
      .then(() => {
        setInitialConfiguration(deepClone(basicConfiguration));
        setShowPendingChanges(false);
        setToaster({
          severity: "success",
          summary: "Success",
          detail: "MortgageClas Validation settings saved successfully.",
        });
      })
      .catch((e: any) => {
        setToaster({
          severity: "error",
          summary: "Save Failed",
          detail: e?.response?.data || "Unable to save settings.",
        });
      })
      .finally(() => setSaving(false));
  };

  const discardChanges = () => {
    if (!hasPendingChanges) {
      return;
    }

    setToaster({
      severity: "info",
      summary: "Discarding Changes",
      detail: "Reloading the latest saved settings from the server.",
    });

    setShowPendingChanges(false);
    getCustomerApplication();
  };

  const encompassPayload =
    basicConfiguration?.encompassPayloadForMissingDocuments || [];

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-2xl font-semibold">
            Manage MortgageClas Validation Settings
          </h3>
        </div>

        <div className="flex items-center gap-2">
          <Button
            label={
              hasPendingChanges
                ? `View Pending Changes (${pendingChangesCount})`
                : "View Pending Changes"
            }
            icon="pi pi-list"
            className="p-button-outlined p-button-secondary p-button-sm"
            disabled={!hasPendingChanges}
            onClick={() => setShowPendingChanges(true)}
          />
          <Button
            label={saving ? "Saving..." : "Save"}
            icon="pi pi-save"
            className="p-button-sm p-button-success"
            disabled={!hasPendingChanges || saving}
            onClick={saveChanges}
          />
          <Button
            label="Discard"
            icon="pi pi-refresh"
            className="p-button-sm p-button-outlined p-button-warning"
            disabled={!hasPendingChanges || saving}
            onClick={discardChanges}
          />
        </div>
      </div>

      {hasPendingChanges && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">
          <div className="font-semibold mb-1">Unsaved changes notice</div>
          <div>
            Changes shown on this page are not saved until you click Save. If you
            leave the page or click Discard, all unsaved changes will be lost.
          </div>
        </div>
      )}

      {(loading || saving) && <Loader />}

      {!loading && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {sectionCards.map((section) => (
            <Card
              key={section.key}
              header={
                <div className="flex flex-col gap-1 pl-4 pt-1 pb-1">
                  <div className="text-lg font-semibold text-gray-900 leading-tight">
                    {section.title}
                  </div>
                  <div className="text-sm font-medium uppercase tracking-wide text-gray-500 leading-none">
                    {section.data.length} item{section.data.length === 1 ? "" : "s"}
                  </div>
                </div>
              }
              className="shadow-md rounded-lg"
            >
              {(section.key === "documentsNotToFeed" || section.key === "excludedDocuments") && (
                <div className="mb-3">
                  <InputText
                    value={searchValues[section.key]}
                    onChange={(e) =>
                      setSearchValues((prev) => ({
                        ...prev,
                        [section.key]: e.target.value,
                      }))
                    }
                    className="w-full"
                    placeholder="Search name"
                  />
                </div>
              )}

              <div className="flex items-center gap-2 mb-3">
                <InputText
                  value={newValues[section.key]}
                  onChange={(e) =>
                    setNewValues((prev) => ({
                      ...prev,
                      [section.key]: e.target.value,
                    }))
                  }
                  className="w-full"
                  placeholder="Enter new value"
                />
                <Button
                  type="button"
                  icon="pi pi-plus"
                  className="p-button-sm p-button-outlined"
                  onClick={() => addNewValue(section.key)}
                  tooltip="Add"
                />
              </div>

              {getVisibleSectionData(section.key, section.data).length === 0 ? (
                <div className="text-gray-500 text-sm">No records found</div>
              ) : (
                <div className="max-h-96 overflow-y-auto pr-2 space-y-2">
                  {getVisibleSectionData(section.key, section.data).map((item: string, index: number) => (
                    <div
                      key={`${section.key}-${index}`}
                      className="flex items-center gap-2"
                    >
                      <div className="w-10 shrink-0 text-sm font-semibold text-gray-600 text-right">
                        {index + 1}.
                      </div>
                      <InputText
                        value={item}
                        className="w-full"
                        onChange={(e) =>
                          updateExistingValue(section.key, index, e.target.value)
                        }
                      />
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-outlined p-button-danger p-button-sm"
                        onClick={() => removeValue(section.key, index)}
                        tooltip="Remove"
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}

          <Card
            title="Encompass Missing Documents Settings"
            className="shadow-md rounded-lg xl:col-span-2"
          >
            <div className="mb-4 text-sm">
              <span className="font-semibold mr-2">
                Update Encompass For Missing Documents:
              </span>
              <span
                className={`font-semibold ${
                  basicConfiguration?.updateEncompassForMissingDocuments
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {basicConfiguration?.updateEncompassForMissingDocuments
                  ? "Yes"
                  : "No"}
              </span>
            </div>

            {encompassPayload.length === 0 ? (
              <div className="text-gray-500 text-sm">No payload records found</div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {encompassPayload.map((item, index) => (
                  <div
                    key={`encompassPayload-${index}`}
                    className="border border-gray-200 rounded-md p-3 bg-gray-50"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="font-semibold">Name</div>
                        <div>{item.name || "-"}</div>
                      </div>
                      <div>
                        <div className="font-semibold">Value</div>
                        <div>{item.value || "-"}</div>
                      </div>
                      <div>
                        <div className="font-semibold">Is Custom Field</div>
                        <div>{item.isCustomField ? "Yes" : "No"}</div>
                      </div>
                      <div>
                        <div className="font-semibold">Append Data</div>
                        <div>{item.appendData ? "Yes" : "No"}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      <Sidebar
        visible={showPendingChanges}
        position="right"
        style={{ width: "36vw", minWidth: "360px" }}
        onHide={() => setShowPendingChanges(false)}
      >
        <div className="p-1">
          <h3 className="text-xl font-semibold mb-4">Pending Changes</h3>

          {pendingChanges.length === 0 ? (
            <div className="text-sm text-gray-500">No pending changes.</div>
          ) : (
            <div className="space-y-4">
              {pendingChanges.map((section) => (
                <Card key={section.key} title={section.title} className="shadow-sm">
                  {section.added.length > 0 && (
                    <div className="mb-3">
                      <div className="text-sm font-semibold text-green-700 mb-1">
                        Added
                      </div>
                      <ul className="list-disc list-inside text-sm">
                        {section.added.map((item: string, index: number) => (
                          <li key={`added-${section.key}-${index}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {section.removed.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold text-red-700 mb-1">
                        Removed
                      </div>
                      <ul className="list-disc list-inside text-sm">
                        {section.removed.map((item: string, index: number) => (
                          <li key={`removed-${section.key}-${index}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </Sidebar>
    </>
  );
};

export default ManageMortgageClasValidationSettings;
